import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { formatCLP, dateLabel } from "../../lib/format";
import { TrendUp, CheckCircle, Receipt, CircleNotch, ArrowRight, MapPin, CaretLeft, CaretRight } from "@phosphor-icons/react";

export default function CarrierEarnings() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;
  useEffect(() => {
    api.get("/carrier/earnings").then((r) => setData(r.data))
      .catch(() => setData({ totalEarned: 0, completedCount: 0, averageTicket: 0, services: [] }));
  }, []);

  if (!data) return <div className="flex justify-center py-24"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>;

  const services = data.services;
  const totalPages = Math.max(1, Math.ceil(services.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const paged = services.slice(start, start + PAGE_SIZE);

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid="carrier-earnings">
      <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A36]">Mis finanzas</span>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1 mb-4">Ganancias</h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <Metric icon={TrendUp} label="Total ganado" value={formatCLP(data.totalEarned)} testid="total-earned" />
        <Metric icon={CheckCircle} label="Servicios completados" value={data.completedCount} testid="completed-count" />
        <Metric icon={Receipt} label="Ticket promedio" value={formatCLP(data.averageTicket)} testid="avg-ticket" />
      </div>

      <h2 className="font-semibold mb-3">Historial de servicios completados</h2>
      {services.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-zinc-300 bg-white p-10 text-center text-zinc-500" data-testid="earnings-empty">
          Aún no tienes servicios completados. Cuando termines un flete aparecerá aquí con su monto.
        </div>
      ) : (
        <>
          <div className="space-y-2" data-testid="earnings-list">
            {paged.map((s) => (
              <Link key={s.id} to={`/app/service/${s.id}`} data-testid={`earning-${s.id}`}
                className="flex items-center justify-between rounded-2xl border border-zinc-200 bg-white px-5 py-3 hover:border-[#FF5A36] transition-colors">
                <div className="min-w-0">
                  <div className="font-semibold flex items-center gap-1.5"><MapPin size={15} className="text-[#FF5A36]" /> {s.origin} → {s.destination}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">{s.category}{s.completedAt ? ` · ${dateLabel(s.completedAt)}` : ""}</div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-display text-xl font-black">{formatCLP(s.agreedPrice)}</span>
                  <ArrowRight size={18} className="text-zinc-400" />
                </div>
              </Link>
            ))}
          </div>
          <div className="flex items-center justify-between mt-3 flex-wrap gap-3" data-testid="earnings-pagination">
            <span className="text-sm text-zinc-500" data-testid="earnings-range">
              Mostrando {start + 1}–{Math.min(start + PAGE_SIZE, services.length)} de {services.length}
            </span>
            <div className="flex items-center gap-2">
              <button data-testid="earnings-prev" disabled={currentPage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-300 text-sm font-semibold text-zinc-600 hover:border-[#0F1115] disabled:opacity-40 disabled:cursor-not-allowed active:scale-95">
                <CaretLeft size={14} weight="bold" /> Anterior
              </button>
              <span className="text-sm font-semibold text-zinc-700" data-testid="earnings-page">Página {currentPage} de {totalPages}</span>
              <button data-testid="earnings-next" disabled={currentPage >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-zinc-300 text-sm font-semibold text-zinc-600 hover:border-[#0F1115] disabled:opacity-40 disabled:cursor-not-allowed active:scale-95">
                Siguiente <CaretRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-5" data-testid={testid}>
      <Icon size={22} className="text-[#FF5A36] mb-2" weight="bold" />
      <div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{label}</div>
      <div className="font-display text-2xl font-black truncate">{value}</div>
    </div>
  );
}
